import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { FontIcon } from '@fluentui/react';
import {
  PagedDataGrid,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { appRoles } from '../../authConfig';
import {
  deleteUser,
  exportUsers,
  getUsers,
  importUsers,
  IUser,
  type IImportUsersResponse,
} from '../../services/userService';
import { profileDataAtom } from '../../atoms/authAtoms';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import { formatImportRowsSuccess } from '../../utils/csvHelpers';
import ImportPanel from '../../common/ImportPanel';
import ImportRowResults from '../../common/ImportRowResults';
import { useExportCsv } from '../../common/useExportCsv';

const USERS_COLUMNS: DataGridColumn<IUser>[] = [
  { key: 'id', name: 'Id', width: 50 },
  { key: 'username', name: 'Username', width: 250, ellipsis: true },
  { key: 'firstName', name: 'First Name', width: 180, ellipsis: true },
  { key: 'lastName', name: 'Last Name', width: 180, ellipsis: true },
  { key: 'email', name: 'Email', width: 200, ellipsis: true },
];

const Users = () => {
  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();
  const setSuccessMessage = useSetAtom(successMessageAtom);

  const [selectedUserId, setSelectedUserId] = useState<number>();
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);

  const isAdmin = profileData.roles.includes(appRoles.Admin);

  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const handleExport = useExportCsv(exportUsers, 'users');

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newUser',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newUser'),
        disabled: !isAdmin,
      },
      {
        key: 'editUser',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => navigate(`/editUser?id=${selectedUserId}`),
        disabled: !isAdmin || !selectedUserId,
      },
      {
        key: 'deleteUser',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: (ctx) => {
          if (!selectedUserId) return;
          const abortController = new AbortController();
          deleteUser(abortController, Number(selectedUserId)).then(() => {
            ctx.refresh();
            setSuccessMessage(`User with id: ${selectedUserId} deleted.`);
          });
        },
        disabled: !isAdmin || !selectedUserId,
      },
      {
        key: 'importUsers',
        text: 'Import',
        icon: <FontIcon iconName="CloudUpload" />,
        onClick: () => setImportPanelOpen(true),
        disabled: !isAdmin,
      },
      {
        key: 'exportUsers',
        text: 'Export',
        icon: <FontIcon iconName="CloudDownload" />,
        onClick: () => {
          void handleExport();
        },
        disabled: !isAdmin,
      },
    ],
    [isAdmin, selectedUserId, navigate, setSuccessMessage, handleExport]
  );

  return (
    <>
      <PagedDataGrid<IUser>
        ref={pagedDataGridRef}
        columns={USERS_COLUMNS}
        getAction={async (abortController, pageSize, pageNo, orderBy, search) =>
          getUsers(abortController, pageSize, pageNo, orderBy, search)
        }
        commandBarItems={commandBarItems}
        selectionMode="single"
        onSelectionChange={(selection) => {
          setSelectedUserId(Number(selection.id));
        }}
        onRowDoubleClicked={(selection) =>
          navigate(`/editUser?id=${selection.id}`)
        }
      />
      <ImportPanel<IImportUsersResponse>
        isOpen={importPanelOpen}
        headerText="Import Users"
        onDismiss={() => setImportPanelOpen(false)}
        importApi={importUsers}
        getErrorsCount={(r) => r.errorsCount}
        onImportSucceeded={(r) =>
          formatImportRowsSuccess(r.importUserResponses)
        }
        onAfterImport={() => pagedDataGridRef.current?.refresh()}
        renderResults={(r) => (
          <ImportRowResults
            rows={r.importUserResponses}
            messages={r.messages}
          />
        )}
      />
    </>
  );
};

export default Users;
